import { faClone, faPencil, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { NestedContentArea } from 'src/components/NestedContentArea';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { Group } from 'src/models/group/_types';
import { fetchGroups } from 'src/models/group/group';
import { Habit } from 'src/models/habit/_types';
import { fetchHabits } from 'src/models/habit/habit';
import { LifecycleStage } from 'src/models/lifecycle/_types';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { WorkoutPlan } from 'src/models/workout-plan/_types';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';
import toast from 'src/utils/toast';

import { AssessmentFormValues } from '../AssessmentNewPage';

import { AssessmentRuleFormula } from './AssessmentRuleFormula';


interface AssessmentRuleProps {
  rule: any;
  onRemove: (rule: any) => Promise<boolean>;
  onClone: (rule: any) => void;
  index: number;
}

export const AssessmentRule = ({
  rule,
  onRemove,
  onClone,
  index,
}: AssessmentRuleProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.rules',
  });
  const { t: tCommon } = useTranslation('translation');
  const { setFieldValue, values } = useFormikContext<AssessmentFormValues>();
  const { session } = useSession();
  const [editName, setEditName] = useState(false);
  const [nameValue, setNameValue] = useState(rule.name);

  // Sync nameValue with rule.name when rule changes
  useEffect(() => {
    if (!editName) {
      setNameValue(rule.name || '');
    }
  }, [rule.name, editName]);

  // Fetch data
  const { data: plans = [] } = useQuery<WorkoutPlan[]>({
    queryKey: ['workoutPlans', 'options'],
    queryFn: fetchWorkoutPlanOptions,
  });

  const { data: groupsData } = useQuery<{ groups: Group[]; total: number }>({
    queryKey: ['groups', 'all'],
    queryFn: () =>
      fetchGroups({ page: 0, perPage: 1000, search: '' }).then((data) => ({
        groups: data.groups,
        total: data.total,
      })),
  });

  const { data: lifecycleStages = [] } = useQuery<LifecycleStage[]>({
    queryKey: ['lifecycleStages', session?.id],
    queryFn: fetchAllLifecycleStages,
    enabled: !!session?.id,
  });

  const { data: habits = [] } = useQuery<Habit[]>({
    queryKey: ['habits'],
    queryFn: fetchHabits,
  });

  const groups = groupsData?.groups || [];
  const clientTags = session?.clientTags || [];
  const weekPlans = plans.filter((p) => p.planType === 'week');
  const allPlans = plans;

  // Result values
  const resultValues = [
    {
      type: 'assign_plan',
      name: t('assignWeekPlan', { plan: tCommon('plan.one') }),
    },
    {
      type: 'plan_to_calendar',
      name: t('schedulePlanToCalendar', { plan: tCommon('plan.one') }),
    },
    {
      type: 'add_group',
      name: t('addToGroup', { group: tCommon('group.one') }),
    },
    {
      type: 'tag_client',
      name: t('tagClient', { client: tCommon('trainer.client.one') }),
    },
    {
      type: 'untag_client',
      name: t('untagClient', { client: tCommon('trainer.client.one') }),
    },
    {
      type: 'add_client',
      name: t('addAsClient', { client: tCommon('trainer.client.one') }),
    },
    {
      type: 'check_in_message',
      name: t('kioskCheckInMessage'),
    },
    {
      type: 'text_message',
      name: t('sendTextMessage'),
    },
    {
      type: 'email',
      name: t('sendEmail'),
    },
    {
      type: 'habit',
      name: t('assignHabit'),
    },
    {
      type: 'lifecycle_stage',
      name: t('setLifecycleStage'),
    },
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Tag options (combine clientTags with rule.result.data if exists)
  const tagOptions = rule.result?.data?.length
    ? [...new Set([...clientTags, ...(rule.result.data || [])])]
    : clientTags;

  const handleRemove = async () => {
    if (await onRemove(rule)) {
      const currentRules = values.rules || [];
      const updatedRules = currentRules.filter((_, i) => i !== index);
      setFieldValue('rules', updatedRules);
      toast.success(t('successfullyDeleted', { name: rule.name }));
    }
  };

  const handleClone = () => {
    onClone(rule);
  };

  const handleNameChange = (newName: string) => {
    const currentRules = values.rules || [];
    const updatedRules = [...currentRules];
    updatedRules[index] = { ...updatedRules[index], name: newName };
    setFieldValue('rules', updatedRules);
    setEditName(false);
  };

  const handleUpdateResultType = (resultType: string) => {
    const currentRules = values.rules || [];
    const updatedRules = [...currentRules];
    const selectedResult = resultValues.find((r) => r.type === resultType);
    if (selectedResult) {
      const newResult: any = {
        type: resultType,
        id: null,
        data: resultType === 'plan_to_calendar' ? 'sunday' : [],
      };
      updatedRules[index] = {
        ...updatedRules[index],
        result: newResult,
      };
      setFieldValue('rules', updatedRules);
    }
  };

  const handleUpdateResultField = (field: string, value: any) => {
    const currentRules = values.rules || [];
    const updatedRules = [...currentRules];
    updatedRules[index] = {
      ...updatedRules[index],
      result: {
        ...updatedRules[index].result,
        [field]: value,
      },
    };
    setFieldValue('rules', updatedRules);
  };

  const handleAddFormula = () => {
    const currentRules = values.rules || [];
    const updatedRules = [...currentRules];
    const currentFormulas = updatedRules[index].formulas || [];
    updatedRules[index] = {
      ...updatedRules[index],
      formulas: [
        ...currentFormulas,
        { lhs: [], rhs: [], next_question_link: 'AND' },
      ],
    };
    setFieldValue('rules', updatedRules);
  };

  const handleRemoveFormula = (formulaIndex: number) => {
    const currentRules = values.rules || [];
    const updatedRules = [...currentRules];
    const currentFormulas = updatedRules[index].formulas || [];
    updatedRules[index] = {
      ...updatedRules[index],
      formulas: currentFormulas.filter((_, i) => i !== formulaIndex),
    };
    setFieldValue('rules', updatedRules);
  };

  const handleUpdateTags = (tags: string[]) => {
    handleUpdateResultField('data', tags);
  };

  const currentResultType = rule.result?.type || '';
  const resultTypeData = rule.result || { type: '', id: null, data: null };
  const characterCount = (resultTypeData.data || '').length || 0;

  const getResultTypeGridSize = () => {
    return { xs: 12, md: 6 };
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          {editName ? (
            <Box>
              <TextField
                placeholder={t('enterName')}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleNameChange(nameValue);
                  }
                }}
              />
              <Button
                onClick={() => {
                  handleNameChange(nameValue);
                }}
              >
                {t('doneEditing')}
              </Button>
              <Button
                onClick={() => {
                  setNameValue(rule.name || '');
                  setEditName(false);
                }}
              >
                {tCommon('cancel')}
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6">
                {rule.name}
                <Button
                  size="small"
                  onClick={() => {
                    setNameValue(rule.name || '');
                    setEditName(true);
                  }}
                >
                  <Icon
                    icon={faPencil}
                    size="small"
                  />
                </Button>
                <Button size="small" onClick={handleRemove}>
                  <Icon
                    icon={faTrash}
                    size="small"
                  />
                </Button>
                <Button size="small" onClick={handleClone}>
                  <Icon
                    icon={faClone}
                    size="small"
                  />
                </Button>
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12 }} spacing={2}>
          <Stack spacing={2}>
            {rule.formulas?.map((formula: any, formulaIndex: number) => (
              <NestedContentArea key={formulaIndex}>
                <AssessmentRuleFormula
                  formula={formula}
                  index={formulaIndex}
                  ruleIndex={index}
                  formulas={rule.formulas || []}
                  onRemove={() => handleRemoveFormula(formulaIndex)}
                />
              </NestedContentArea>
            ))}
            <Button sx={{ width: 'fit-content' }} onClick={handleAddFormula}>
              <Icon icon={faPlus} size="small" />
              {t('addFormula')}
            </Button>
          </Stack>
          <Box>
            <Grid container spacing={2}>
              <Grid size={getResultTypeGridSize()}>
                <Typography variant="body2">
                  {rule.name} {t('result')}:
                </Typography>
                <TextField
                  value={currentResultType}
                  onChange={(e) => handleUpdateResultType(e.target.value)}
                  options={[
                    {
                      type: '',
                      name: t('selectAction'),
                    },
                    ...resultValues,
                  ].map((r) => ({
                    value: r.type,
                    label: r.name,
                  }))}
                  placeholder={t('selectAction')}
                  fullWidth
                />
              </Grid>
              {(currentResultType === 'assign_plan' ||
                currentResultType === 'plan_to_calendar') && (
                <Grid size={getResultTypeGridSize()}>
                  <Typography>
                    {t('choosePlan', { plan: tCommon('plan.one') })}
                  </Typography>
                  <TextField
                    value={resultTypeData.id || ''}
                    onChange={(e) =>
                      handleUpdateResultField('id', e.target.value)
                    }
                    options={
                      currentResultType === 'assign_plan'
                        ? allPlans.map((p) => ({
                            value: String(p.id),
                            label: p.name,
                          }))
                        : weekPlans.map((p) => ({
                            value: String(p.id),
                            label: p.name,
                          }))
                    }
                    placeholder={t('selectPlan', { plan: tCommon('plan.one') })}
                    fullWidth
                  />
                </Grid>
              )}
              {currentResultType === 'lifecycle_stage' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography>{t('chooseLifecycleStage')}</Typography>
                  {lifecycleStages.length === 0 ? (
                    <Box>{t('loading')}</Box>
                  ) : (
                    <TextField
                      value={resultTypeData.id || ''}
                      onChange={(e) =>
                        handleUpdateResultField('id', e.target.value)
                      }
                      options={lifecycleStages.map((s) => ({
                        value: String(s.id),
                        label: s.name,
                      }))}
                      placeholder={t('selectStage')}
                      fullWidth
                    />
                  )}
                </Grid>
              )}
              {currentResultType === 'add_group' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography>
                    {t('chooseGroup', { group: tCommon('group.one') })}
                  </Typography>
                  {groups.length === 0 ? (
                    <Box>{t('loading')}</Box>
                  ) : (
                    <TextField
                      value={resultTypeData.id || ''}
                      onChange={(e) =>
                        handleUpdateResultField('id', e.target.value)
                      }
                      options={groups.map((g) => ({
                        value: String(g.id),
                        label: g.name,
                      }))}
                      placeholder={t('selectGroup', { group: tCommon('group.one') })}
                      fullWidth
                    />
                  )}
                </Grid>
              )}
              {(currentResultType === 'tag_client' ||
                currentResultType === 'untag_client') && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography>{t('chooseTags')}</Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={tagOptions}
                    value={resultTypeData.data || []}
                    onChange={(_event, newValue) => {
                      handleUpdateTags(newValue as string[]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('selectOrTypeTags')}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              )}
              {currentResultType === 'check_in_message' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography>
                    {t('enterCheckInMessage', {
                      assessment: tCommon('assessment.one').toLowerCase(),
                    })}
                  </Typography>
                  <TextField
                    value={resultTypeData.data || ''}
                    onChange={(e) =>
                      handleUpdateResultField('data', e.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Grid>
              )}
              {currentResultType === 'email' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography>{t('recipientEmailAddress')}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('separateMultipleAddresses')}
                    </Typography>
                    <TextField
                      value={resultTypeData.id || ''}
                      onChange={(e) =>
                        handleUpdateResultField('id', e.target.value)
                      }
                      placeholder={t('emailPlaceholder')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography>{t('enterMessage')}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('nameEmailIncludedByDefault')}
                    </Typography>
                    <TextField
                      value={resultTypeData.data || ''}
                      onChange={(e) =>
                        handleUpdateResultField('data', e.target.value)
                      }
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {currentResultType === 'habit' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography>{t('chooseHabit')}</Typography>
                  {habits.length === 0 ? (
                    <Box>{t('loading')}</Box>
                  ) : (
                    <TextField
                      value={resultTypeData.id || ''}
                      onChange={(e) =>
                        handleUpdateResultField('id', e.target.value)
                      }
                      options={habits.map((h) => ({
                        value: String(h.id),
                        label: h.name,
                      }))}
                      placeholder={t('selectHabit')}
                      fullWidth
                    />
                  )}
                </Grid>
              )}
              {currentResultType === 'text_message' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography>{t('textMessageRecipient')}</Typography>
                    <TextField
                      value={resultTypeData.id || ''}
                      onChange={(e) =>
                        handleUpdateResultField('id', e.target.value)
                      }
                      placeholder={t('phoneNumber')}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography>{t('enterTextMessage')}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      <Typography component="span" fontWeight="bold">
                        {t('defaultMessage')}:
                      </Typography>{' '}
                      {t('defaultMessageContent')}
                    </Typography>
                    <TextField
                      value={resultTypeData.data || ''}
                      onChange={(e) =>
                        handleUpdateResultField('data', e.target.value)
                      }
                      multiline
                      rows={3}
                      fullWidth
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('characterCount')}: {characterCount}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
